import React, { useState, useEffect } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import img from "../../../Images/LoginImages/login_showroom_hud.png";
import logo from "../../../Images/logo.png";
import { login } from "../../../Redux/authSlice";
import { saveToken, saveUser } from "../../Avatar/Utility";
import useApiFetcher from "../../../hooks/useApiFetcher";
import BouncingDotsLoader from "../../Loaders/BouncingDotsLoader";

const Login = () => {
  const { apiFetcher } = useApiFetcher();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Set html and body background to dark space blue to cover full page height when body is zoomed to 80%
  useEffect(() => {
    const originalHtmlBg = document.documentElement.style.background;
    const originalBodyBg = document.body.style.background;

    document.documentElement.style.setProperty("background-color", "#0B0F19", "important");
    document.body.style.setProperty("background-color", "#0B0F19", "important");
    document.documentElement.style.setProperty("background", "#0B0F19", "important");
    document.body.style.setProperty("background", "#0B0F19", "important");

    return () => {
      document.documentElement.style.background = originalHtmlBg;
      document.body.style.background = originalBodyBg;
    };
  }, []);

  const handleValueChange = (e) => {
    setEmail(e.target.value.replace(/\s/g, ''));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value.replace(/\s/g, ''));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiFetcher("/auth/login", "POST", { email, password });
      const token = response?.token;
      const user = response?.user;
      if (token && user) {
        dispatch(login({ token, user }));
        saveUser(user);
        saveToken(token);
      } else {
        console.error("Login failed: Token not received");
      }
    } catch (error) {
      console.error("Login failed:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = (e) => {
    e.preventDefault();
    navigate("/forgotPassword");
  };

  const handleKeyDown = (event) => {
    if (event.key === ' ') {
      event.preventDefault(); 
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4 relative overflow-hidden font-sans">
      
      {/* Background ambient glowing spheres */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-3/4 h-3/4 bg-indigo-500/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-1/4 -right-1/4 w-3/4 h-3/4 bg-cyan-500/10 rounded-full blur-[120px]"></div>
        
        {/* Subtle grid line overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{
            backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }}
        ></div>
      </div>

      {/* Glassmorphic Login Container Card */}
      <div className="relative w-full max-w-5xl h-[600px] flex rounded-3xl overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] shadow-[0_20px_50px_rgba(0,0,0,0.3)] z-10">
        
        {/* Left Section - Holographic/Futuristic Showroom Visual */}
        <div className="hidden md:flex w-1/2 relative items-center justify-center overflow-hidden border-r border-white/[0.08]">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/45 to-transparent z-10"></div>
          <img src={img} alt="Futuristic Showroom" className="absolute inset-0 w-full h-full object-cover" />
          
          <div className="relative z-20 text-center p-8 mt-auto mb-10 text-white">
             <h3 className="text-3xl font-extrabold mb-2 tracking-wide text-white drop-shadow-md">Welcome Back</h3>
             <p className="text-slate-300 text-sm max-w-xs mx-auto leading-relaxed drop-shadow">
               Log in to manage your AI avatars, voice agents, and campaigns.
             </p>
          </div>
        </div>

        {/* Right Section - Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12">
          <div className="max-w-md w-full mx-auto">
            
            {/* Logo Area */}
            <div className="flex items-center gap-2 mb-6 justify-center md:justify-start">
              <img src={logo} alt="Sailsense AI" className="h-7 w-auto object-contain" />
              <span className="text-xl font-bold text-white tracking-wide">
                Sailsense <span className="text-cyan-400">AI</span>
              </span>
            </div>

            <h2 className="text-3xl font-bold text-white mb-1.5 text-center md:text-left">Sign In</h2>
            <p className="text-slate-400 mb-8 text-center md:text-left text-sm">
              Access your Sailsense AI dashboard
            </p>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Email</label>
                <input
                  type="text"
                  placeholder="Enter your email"
                  value={email}
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all duration-300 text-white placeholder-slate-500 text-sm"
                  onKeyDown={handleKeyDown}
                  onChange={handleValueChange}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all duration-300 text-white placeholder-slate-500 text-sm pr-12"
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-4 text-slate-400 hover:text-cyan-400 transition-colors focus:outline-none"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <AiFillEyeInvisible size={18} /> : <AiFillEye size={18} />}
                  </button>
                </div>
                
                <div className="flex justify-end mt-1">
                  <button 
                    type="button" 
                    onClick={handleForgot} 
                    className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors focus:outline-none"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="mt-4 py-3 flex justify-center items-center h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-slate-950 font-bold text-sm tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.15)] hover:shadow-[0_0_25px_rgba(6,182,212,0.3)] transition-all duration-300 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <BouncingDotsLoader />
                ) : (
                  <span>Log In</span>
                )}
              </button>
              
            </form>

            <div className="mt-8 pt-6 border-t border-white/[0.08]">
               <p className="text-[10px] text-center text-slate-500 leading-relaxed uppercase tracking-wider">
                  By continuing, you agree to our{" "}
                  <a href="/terms" className="text-slate-400 hover:text-cyan-400 transition-colors underline">Terms</a>,{" "}
                  <a href="/privacy" className="text-slate-400 hover:text-cyan-400 transition-colors underline">Privacy</a>, and{" "}
                  <a href="/content" className="text-slate-400 hover:text-cyan-400 transition-colors underline">Content Policy</a>.
               </p>
            </div>
            
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
